// =====================================================================
// PLENARIUM:CONDUCAO -- condução da sessão pelo administrador
// Barra fixa no alto da tela da sessão:
//  - as etapas na ordem da sessão (conforme a Estrutura da câmara):
//    Abertura › Chamada › Ata › Expediente › Tribuna › (2ª chamada) ›
//    Ordem do Dia › Explicações Pessoais › Encerramento
//  - botão "Próximo passo" e clique direto em qualquer etapa
//  - "Na TV": Automático, Presença, Fase atual, Ordem do Dia, Resultado,
//    Boas-vindas e Agradecimento
//  - as ferramentas de antes (Painel TV, Cronômetro, Presença, 2ª chamada,
//    Roteiro, Estrutura, Lista de Presença)
// Votação em andamento e cronômetro sempre aparecem na TV sozinhos.
// =====================================================================
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import toast from 'react-hot-toast';
import { io } from 'socket.io-client';
import { Play, Square, Tv, Timer, UserCheck, ScrollText, Printer, Check, ChevronRight, Radio } from 'lucide-react';
import { BotaoEstruturaSessao, useEstruturaSessao } from './EstruturaSessao';
import { BotaoSegundaChamada, podeIniciarOrdemDoDia, confirmaQuorum } from './Chamadas'; // PLENARIUM:PRESENCA_REGISTRO
import { ControleMateriaTV } from './MateriaTV'; // PLENARIUM:TV_SOBERANA

const API = process.env.REACT_APP_API_URL || 'http://localhost:3001';
const erroDe = (e, p) => (e && e.response && e.response.data && e.response.data.error) || p;

export function montarEtapas(estrutura) {
  const unico = estrutura && estrutura.expediente === 'UNICO';
  const duas = estrutura && estrutura.chamadas === 'DUAS';
  const e = [
    { fase: 'ABERTURA', rot: 'Abertura', secao: 'abertura', tela: 'AUTO', titulo: 'Abertura da sessão' },
    { fase: 'CHAMADA', rot: duas ? '1ª chamada' : 'Chamada', secao: 'abertura', tela: 'PRESENCA', titulo: 'Chamada de presença' },
    { fase: 'ATA', rot: 'Ata', secao: 'ata', tela: 'FASE', titulo: 'Leitura da Ata', subtitulo: 'Ata da sessão anterior: leitura, discussão e votação' },
    { fase: 'EXPEDIENTE', rot: unico ? 'Expediente' : 'Pequeno Expediente', secao: 'expediente', tela: 'FASE', titulo: unico ? 'Expediente' : 'Pequeno Expediente', subtitulo: 'Leitura das matérias, ofícios e comunicações' },
    unico
      ? { fase: 'TRIBUNA_EXP', rot: 'Tribuna', secao: 'tribuna', tela: 'FASE', titulo: 'Expediente', subtitulo: 'Uso da tribuna pelos vereadores inscritos' }
      : { fase: 'TRIBUNA_PE', rot: 'Tribuna (Pequeno)', secao: 'tribuna', tela: 'FASE', titulo: 'Pequeno Expediente', subtitulo: 'Uso da tribuna pelos vereadores inscritos' },
    !unico && { fase: 'TRIBUNA_GE', rot: 'Grande Expediente', secao: 'tribuna', tela: 'FASE', titulo: 'Grande Expediente', subtitulo: 'Uso da tribuna pelos vereadores inscritos' },
    duas && { fase: 'CHAMADA2', rot: '2ª chamada', secao: 'abertura', tela: 'PRESENCA', titulo: '2ª chamada' },
    { fase: 'ORDEM_DIA', rot: 'Ordem do Dia', secao: 'ordem', tela: 'AUTO', titulo: 'Ordem do Dia', subtitulo: 'Discussão e votação das matérias' },
    { fase: 'EXPLICACOES', rot: 'Explicações Pessoais', secao: 'tribuna', tela: 'FASE', titulo: 'Explicações Pessoais', subtitulo: 'Uso da tribuna pelos vereadores inscritos' },
    { fase: 'ENCERRAMENTO', rot: 'Encerramento', secao: 'encerramento', tela: 'AUTO', titulo: 'Encerramento' }
  ];
  return e.filter(Boolean);
}

const CSS = `
.pcs { position: sticky; top: var(--header-altura, 56px); z-index: 40; margin: 0 -28px 16px; padding: 10px 28px 12px; background: var(--fundo, #f1f4f9); }
.pcs-card { position: relative; background: linear-gradient(120deg, #e8f0ff 0%, #f1f5ff 50%, #f4f1ff 100%); border: 1px solid #c9d8f6; border-radius: 16px; box-shadow: 0 6px 18px rgba(30,58,138,.08); padding: 12px 14px; display: flex; flex-direction: column; gap: 10px; } /* PLENARIUM:CONDUCAO_COR2 -- destaque em azul claro */
.pcs-etapa:not(.feita):not(.atual) { background: #fff; border-color: #d4e0f7; }
.pcs-ferr .btn { background: #fff; border-color: #d4e0f7; }
.pcs-tv { border-top: 1px solid #d4e0f7 !important; }
.pcs-tv button:not(.on) { background: #fff; border-color: #d4e0f7; }
.pcs-linha { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; }
.pcs-status { display: inline-flex; align-items: center; gap: 6px; border-radius: 999px; padding: 5px 11px; font-size: .74rem; font-weight: 800; letter-spacing: .04em; text-transform: uppercase; flex-shrink: 0; }
.pcs-status i { width: 8px; height: 8px; border-radius: 50%; background: currentColor; }
.pcs-status.vivo i { animation: pcsPulso 1.4s infinite; }
@keyframes pcsPulso { 0%,100% { opacity: 1 } 50% { opacity: .3 } }
.pcs-etapas { display: flex; align-items: center; gap: 4px; flex-wrap: wrap; row-gap: 6px; flex: 1; min-width: 0; }
.pcs-etapa { display: inline-flex; align-items: center; gap: 6px; border: 1px solid #e2e8f0; background: #fff; color: #475569; border-radius: 999px; padding: 5px 11px 5px 6px; font: inherit; font-size: .78rem; font-weight: 700; cursor: pointer; white-space: nowrap; flex-shrink: 0; }
.pcs-etapa:hover { border-color: #93c5fd; }
.pcs-etapa b { width: 20px; height: 20px; border-radius: 50%; background: #f1f5f9; color: #64748b; display: inline-flex; align-items: center; justify-content: center; font-size: .7rem; }
.pcs-etapa.feita { color: #15803d; border-color: #bbf7d0; background: #f0fdf4; }
.pcs-etapa.feita b { background: #16a34a; color: #fff; }
.pcs-etapa.atual { color: #fff; background: #1d4ed8; border-color: #1d4ed8; box-shadow: 0 0 0 3px rgba(29,78,216,.18); }
.pcs-etapa.atual b { background: #fff; color: #1d4ed8; }
.pcs-etapa:disabled { cursor: default; opacity: .55; }
.pcs-seta { color: #cbd5e1; flex-shrink: 0; }
.pcs-agora { font-size: .8rem; color: #64748b; }
.pcs-agora b { color: #0f172a; }
.pcs-prox { display: inline-flex; align-items: center; gap: 8px; background: #1d4ed8; color: #fff; border: 0; border-radius: 12px; padding: 9px 16px; font: inherit; font-size: .9rem; font-weight: 800; cursor: pointer; box-shadow: 0 4px 12px rgba(29,78,216,.25); }
.pcs-prox:disabled { opacity: .6; cursor: default; }
.pcs-prox.verde { background: #16a34a; box-shadow: 0 4px 12px rgba(22,163,74,.25); }
.pcs-prox.verm { background: #dc2626; box-shadow: 0 4px 12px rgba(220,38,38,.25); }
.pcs-ferr { display: flex; gap: 6px; flex-wrap: wrap; margin-left: auto; }
.pcs-ferr .btn { padding: 6px 10px; font-size: .78rem; }
.pcs-tv { display: flex; align-items: center; gap: 6px; flex-wrap: wrap; padding-top: 10px; border-top: 1px dashed #e2e8f0; }
.pcs-tv-rot { display: inline-flex; align-items: center; gap: 6px; font-size: .74rem; font-weight: 800; color: #6d28d9; text-transform: uppercase; letter-spacing: .05em; margin-right: 2px; }
.pcs-tv button { border: 1px solid #e2e8f0; background: #fff; color: #334155; border-radius: 999px; padding: 5px 11px; font: inherit; font-size: .78rem; font-weight: 700; cursor: pointer; }
.pcs-tv button.on { background: #6d28d9; border-color: #6d28d9; color: #fff; }
.pcs-tv button:disabled { opacity: .45; cursor: default; }
.pcs-tv select { border: 1px solid #e2e8f0; border-radius: 999px; padding: 4px 8px; font: inherit; font-size: .76rem; max-width: 240px; }
.pcs-tv .pcs-aviso { font-size: .72rem; color: #64748b; margin-left: auto; }
.pcs-aoVivo { display: inline-flex; align-items: center; gap: 6px; background: #fee2e2; color: #b91c1c; border-radius: 999px; padding: 4px 10px; font-size: .74rem; font-weight: 800; }
.psf { display: flex; align-items: center; gap: 10px; margin: 26px 0 12px; scroll-margin-top: 230px; }
.psf .n { width: 28px; height: 28px; border-radius: 9px; background: #1d4ed8; color: #fff; display: flex; align-items: center; justify-content: center; font-weight: 900; font-size: .85rem; flex-shrink: 0; }
.psf b { font-size: 1.02rem; color: #0f172a; }
.psf small { display: block; font-size: .76rem; color: #64748b; font-weight: 500; }
.psf::after { content: ''; flex: 1; height: 1px; background: #e2e8f0; }
.psf.atual .n { box-shadow: 0 0 0 4px rgba(29,78,216,.2); }
@media (max-width: 900px) { .pcs { position: static; margin: 0 0 12px; padding: 0; } .pcs-etapas { flex-wrap: nowrap; overflow-x: auto; padding-bottom: 4px; } .pcs-ferr { margin-left: 0; } .psf { scroll-margin-top: 70px; } }
`;

// Cabecalho de cada parte da tela, na ordem da sessao
export function SecaoFase({ id, n, titulo, desc }) {
  return (
    <div className="psf" id={`pcs-sec-${id}`}>
      <style>{CSS}</style>
      <span className="n">{n}</span>
      <span><b>{titulo}</b>{desc && <small>{desc}</small>}</span>
    </div>
  );
}

const ir = (secao) => { const el = document.getElementById(`pcs-sec-${secao}`); if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' }); };

export default function ConducaoSessao({ sessao, sessaoId, pautas, onIniciar, onEncerrar, alternarOrdemDoDia, abrirPresenca, abrirCronometro, onRoteiro, onListaPresenca }) {
  const estrutura = useEstruturaSessao();
  const etapas = useMemo(() => montarEtapas(estrutura), [estrutura]);
  const [cond, setCond] = useState({ fase: null, tela: { modo: 'AUTO', dados: null } });
  const [ocupado, setOcupado] = useState(false);
  const status = sessao && sessao.status;

  const buscar = useCallback(() => {
    if (!sessaoId) return;
    axios.get(`/api/conducao/sessao/${sessaoId}`).then(r => setCond({ fase: r.data.fase, tela: r.data.tela || { modo: 'AUTO', dados: null } })).catch(() => {});
  }, [sessaoId]);
  useEffect(() => { buscar(); }, [buscar]);
  useEffect(() => {
    if (!sessaoId) return undefined;
    const s = io(API);
    s.on('connect', () => s.emit('entrar-sala', `sessao-${sessaoId}`));
    s.on('conducao-atualizada', (d) => { if (d && d.sessaoId === sessaoId) setCond({ fase: d.fase, tela: d.tela }); });
    return () => s.disconnect();
  }, [sessaoId]);

  const gravar = async (corpo) => {
    const r = await axios.put(`/api/conducao/sessao/${sessaoId}`, corpo);
    setCond({ fase: r.data.fase, tela: r.data.tela });
    return r.data;
  };
  const telaDaEtapa = (et) => (et.tela === 'FASE' ? { modo: 'FASE', dados: { titulo: et.titulo, subtitulo: et.subtitulo || '' } } : { modo: et.tela, dados: null });

  // Fase atual: a gravada; sem ela, deduz pelo estado da sessao
  const faseAtual = cond.fase || (status === 'ENCERRADA' ? 'ENCERRADA' : status === 'EM_ANDAMENTO' ? (sessao.ordemDoDiaIniciada ? 'ORDEM_DIA' : 'CHAMADA') : null);
  const iAtual = status === 'ENCERRADA' ? etapas.length : etapas.findIndex(e => e.fase === faseAtual);
  const etapasRef = React.useRef(null);
  useEffect(() => { const c = etapasRef.current; const a = c && c.querySelector('.pcs-etapa.atual'); if (c && a && c.scrollWidth > c.clientWidth) c.scrollLeft = a.offsetLeft - c.clientWidth / 2 + a.offsetWidth / 2; }, [iAtual, etapas.length]);
  const proxima = status === 'AGENDADA' ? etapas[0] : status === 'EM_ANDAMENTO' ? etapas[Math.min(iAtual + 1, etapas.length - 1)] : null;

  const executar = async (et) => {
    if (ocupado) return;
    if (status === 'ENCERRADA') { toast('Sessão encerrada.'); return; }
    if (status === 'AGENDADA' && et.fase !== 'ABERTURA') { toast.error('Inicie a sessão primeiro (etapa 1).'); return; }
    setOcupado(true);
    try {
      if (et.fase === 'ABERTURA') {
        if (status === 'AGENDADA') {
          if (!window.confirm('Iniciar a sessão agora? A TV mostra as boas-vindas e os tablets passam a pedir a presença.')) return;
          await onIniciar();
          await gravar({ fase: 'CHAMADA', tela: { modo: 'PRESENCA', dados: null } });
          ir('abertura');
          return;
        }
      }
      // PLENARIUM:PRESENCA_REGISTRO -- saindo da chamada: confere o quorum de abertura (avisa; o Presidente decide)
      if (status === 'EM_ANDAMENTO' && faseAtual === 'CHAMADA' && etapas.indexOf(et) > iAtual && !(await confirmaQuorum(sessaoId, 'abertura'))) return;
      if (et.fase === 'ORDEM_DIA' && !sessao.ordemDoDiaIniciada) {
        if (!(await podeIniciarOrdemDoDia(sessao))) return;
        await alternarOrdemDoDia(true);
      }
      if (et.fase === 'CHAMADA2') {
        try { await axios.post(`/api/chamadas/sessao/${sessaoId}/abrir`); toast.success('2ª chamada aberta: os tablets pedem a confirmação.'); }
        catch (e) { toast.error(erroDe(e, 'Não foi possível abrir a 2ª chamada.')); return; }
      }
      if (et.fase === 'ENCERRAMENTO') {
        if (!window.confirm('Encerrar a sessão agora? A TV mostra o agradecimento.')) return;
        await gravar({ fase: 'ENCERRAMENTO', tela: { modo: 'AUTO', dados: null } });
        await onEncerrar();
        return;
      }
      await gravar({ fase: et.fase, tela: telaDaEtapa(et) });
      if (et.fase === 'CHAMADA' && abrirPresenca) abrirPresenca();
      ir(et.secao);
    } catch (e) { toast.error(erroDe(e, 'Não foi possível avançar.')); }
    finally { setOcupado(false); }
  };

  const votadas = (pautas || []).filter(p => p.status === 'APROVADA' || p.status === 'REPROVADA')
    .sort((a, b) => new Date(b.encerradaEm || 0) - new Date(a.encerradaEm || 0) || (b.ordem || 0) - (a.ordem || 0));
  const emVotacao = (pautas || []).find(p => p.status === 'EM_VOTACAO');
  const etAtual = etapas.find(e => e.fase === faseAtual);
  const mudarTela = async (modo, dados) => {
    try {
      if (modo === 'FASE') {
        const et = etAtual && etAtual.tela === 'FASE' ? etAtual : etAtual;
        await gravar({ tela: { modo: 'FASE', dados: { titulo: (et && et.titulo) || 'Sessão em andamento', subtitulo: (et && et.subtitulo) || '' } } });
      } else await gravar({ tela: { modo, dados: dados || null } });
    } catch (e) { toast.error(erroDe(e, 'Não foi possível mudar a TV.')); }
  };
  const cerimonial = async (qual) => { try { await gravar({ cerimonial: qual }); toast.success(qual === 'abertura' ? 'Boas-vindas na TV.' : 'Agradecimento na TV.'); } catch (e) { toast.error(erroDe(e, 'Não foi possível.')); } };
  const modo = cond.tela && cond.tela.modo;
  const ST = { AGENDADA: ['Agendada', '#1d4ed8', '#dbeafe'], EM_ANDAMENTO: ['Em andamento', '#15803d', '#dcfce7'], ENCERRADA: ['Encerrada', '#475569', '#f1f5f9'] };
  const st = ST[status] || ST.AGENDADA;

  return (
    <div className="pcs">
      <style>{CSS}</style>
      <div className="pcs-card">
        <div className="pcs-linha">
          <span className={`pcs-status ${status === 'EM_ANDAMENTO' ? 'vivo' : ''}`} style={{ color: st[1], background: st[2] }}><i />{st[0]}</span>
          <div className="pcs-etapas" ref={etapasRef}>
            {etapas.map((et, i) => (
              <React.Fragment key={et.fase}>
                {i > 0 && <ChevronRight size={14} className="pcs-seta" />}
                <button type="button" title={`Ir para: ${et.rot}`} disabled={ocupado || status === 'ENCERRADA'} onClick={() => executar(et)}
                  className={`pcs-etapa ${i < iAtual ? 'feita' : ''} ${i === iAtual && status === 'EM_ANDAMENTO' ? 'atual' : ''}`}>
                  <b>{i < iAtual ? <Check size={12} /> : i + 1}</b>{et.rot}
                </button>
              </React.Fragment>
            ))}
          </div>
        </div>

        <div className="pcs-linha">
          {status === 'EM_ANDAMENTO' && <span className="pcs-agora">Agora: <b>{(etAtual && etAtual.rot) || '—'}</b></span>}
          {proxima && status === 'AGENDADA' && <button type="button" className="pcs-prox verde" disabled={ocupado} onClick={() => executar(proxima)}><Play size={16} /> Iniciar sessão</button>}
          {proxima && status === 'EM_ANDAMENTO' && iAtual < etapas.length - 1 && (
            <button type="button" className={`pcs-prox ${proxima.fase === 'ENCERRAMENTO' ? 'verm' : ''}`} disabled={ocupado} onClick={() => executar(proxima)}>
              {proxima.fase === 'ENCERRAMENTO' ? <Square size={15} /> : <ChevronRight size={16} />} Próximo: {proxima.rot}
            </button>
          )}
          {status === 'EM_ANDAMENTO' && iAtual === etapas.length - 1 && <button type="button" className="pcs-prox verm" disabled={ocupado} onClick={() => executar(etapas[etapas.length - 1])}><Square size={15} /> Encerrar sessão</button>}
          {emVotacao && <span className="pcs-aoVivo"><Radio size={13} /> Votação em andamento: {emVotacao.numero}</span>}
          <div className="pcs-ferr">
            {status === 'EM_ANDAMENTO' && <Link to={`/tv/${sessaoId}?camaraId=${sessao.camaraId}`} target="_blank" className="btn btn-secondary"><Tv size={14} /> Painel TV</Link>}
            {status === 'EM_ANDAMENTO' && <button type="button" className="btn btn-secondary" onClick={abrirCronometro}><Timer size={14} /> Cronômetro</button>}
            <button type="button" className="btn btn-secondary" onClick={abrirPresenca}><UserCheck size={14} /> Presença</button>
            <BotaoSegundaChamada sessao={sessao} />
            <button type="button" className="btn btn-secondary" onClick={onRoteiro}><ScrollText size={14} /> Roteiro</button>
            <BotaoEstruturaSessao />
            {status === 'ENCERRADA' && <button type="button" className="btn btn-secondary" onClick={onListaPresenca}><Printer size={14} /> Lista de Presença</button>}
          </div>
        </div>

        {status === 'EM_ANDAMENTO' && (
          <div className="pcs-tv">
            <span className="pcs-tv-rot"><Tv size={14} /> Na TV:</span>
            {[['AUTO', 'Automático'], ['PRESENCA', 'Presença'], ['FASE', `Fase: ${(etAtual && etAtual.titulo) || 'atual'}`], ['ORDEM_DIA', 'Ordem do Dia']].map(([k, rot]) => (
              <button key={k} type="button" className={modo === k ? 'on' : ''} onClick={() => mudarTela(k)}>{rot}</button>
            ))}
            <button type="button" className={modo === 'RESULTADO' ? 'on' : ''} disabled={!votadas.length} onClick={() => mudarTela('RESULTADO', { pautaId: votadas[0].id })}>Último resultado</button>
            {modo === 'RESULTADO' && votadas.length > 1 && (
              <select value={(cond.tela.dados && cond.tela.dados.pautaId) || ''} onChange={e => mudarTela('RESULTADO', { pautaId: e.target.value })}>
                {votadas.map(p => <option key={p.id} value={p.id}>{p.numero} — {p.status === 'APROVADA' ? 'aprovada' : 'rejeitada'}</option>)}
              </select>
            )}
            {modo === 'MATERIA' && <ControleMateriaTV sessaoId={sessaoId} dados={cond.tela.dados} onSair={() => mudarTela('AUTO')} />}{/* PLENARIUM:TV_SOBERANA */}
            <button type="button" onClick={() => cerimonial('abertura')}>Boas-vindas</button>
            <button type="button" onClick={() => cerimonial('encerramento')}>Agradecimento</button>
            <span className="pcs-aviso">A sua escolha manda na TV. Abrir votação, cronômetro, tribuna ou leitura volta para o Automático.</span>{/* PLENARIUM:TV_SOBERANA */}
          </div>
        )}
      </div>
    </div>
  );
}
